import React, { useState } from 'react';
import { Button, Modal } from 'react-bootstrap';
import { LANGUAGES } from './RecommendationCropDetail';

const emptyI18n = () => Object.fromEntries(LANGUAGES.map((l) => [l, '']));

const CropAllList = ({ items, states, onAdd, onUpdate, onDelete, viewMode }) => {
  const [showModal, setShowModal] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState({
    input_i18n: emptyI18n(),
    constant_dose: false,
    dose: '',
    per_session_dose: '',
    per_session_dose_is_array: false,
    unit_i18n: emptyI18n(),
    method_i18n: emptyI18n(),
    stage_indices: [1],
  });
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState('');
  const [stageIndicesInput, setStageIndicesInput] = useState('');

  const openAdd = () => {
    setEditingId(null);
    setForm({
      input_i18n: emptyI18n(),
      constant_dose: false,
      dose: '',
      per_session_dose: '',
      per_session_dose_is_array: false,
      unit_i18n: emptyI18n(),
      method_i18n: emptyI18n(),
      stage_indices: [1],
      state_id: '',
      min_age: '',
      max_age: '',
    });
    setStageIndicesInput('1');
    setShowModal(true);
    setMessage('');
  };

  const openEdit = (item) => {
    setEditingId(item.id);
    const indices = Array.isArray(item.stage_indices) ? item.stage_indices : (item.stage_indices ? [item.stage_indices] : [1]);
    setForm({
      input_i18n: item.input_i18n || emptyI18n(),
      constant_dose: item.constant_dose ?? false,
      dose: item.dose || '',
      per_session_dose: Array.isArray(item.per_session_dose) ? item.per_session_dose : (item.per_session_dose ?? ''),
      per_session_dose_is_array: item.per_session_dose_is_array ?? false,
      unit_i18n: item.unit_i18n || emptyI18n(),
      method_i18n: item.method_i18n || emptyI18n(),
      stage_indices: indices,
      state_id: item.state_id || '',
      min_age: item.min_age ?? '',
      max_age: item.max_age ?? '',
    });
    setStageIndicesInput(indices.join(', '));
    setShowModal(true);
    setMessage('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setMessage('');
    try {
      const payload = { ...form };
      const parsedIndices = stageIndicesInput.split(',').map((s) => parseInt(s.trim(), 10)).filter((n) => !Number.isNaN(n));
      payload.stage_indices = parsedIndices.length ? parsedIndices : [1];
      if (payload.per_session_dose_is_array && typeof payload.per_session_dose === 'string') {
        try {
          payload.per_session_dose = JSON.parse(payload.per_session_dose);
        } catch (_) {
          payload.per_session_dose = payload.per_session_dose.split(',').map((n) => parseFloat(n.trim())).filter((n) => !Number.isNaN(n));
        }
      }
      if (editingId) await onUpdate(editingId, payload);
      else await onAdd(payload);
      setShowModal(false);
    } catch (err) {
      setMessage(err.message || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const setI18n = (field, lang, value) => {
    setForm((prev) => ({
      ...prev,
      [field]: { ...prev[field], [lang]: value },
    }));
  };

  const visibleLangs = LANGUAGES;

  return (
    <div className="card p-3">
      <div className="d-flex justify-content-between align-items-center mb-3">
        <h5 className="mb-0">All list (common prescriptions)</h5>
        {!viewMode && (
          <Button variant="primary" size="sm" onClick={openAdd}>
            Add item
          </Button>
        )}
      </div>
      {message && <div className="alert alert-warning small">{message}</div>}
      {(!items || items.length === 0) ? (
        <p className="text-muted">No items. Add common prescriptions that apply regardless of nutrient level.</p>
      ) : (
        <div className="table-responsive">
          <table className="table table-sm">
            <thead>
              <tr>
                <th>#</th>
                <th>Input (EN)</th>
                <th>Dose</th>
                <th>State</th>
                <th>Age Range</th>
                <th>Stages</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {items.map((item, i) => (
                <tr key={item.id}>
                  <td>{i + 1}</td>
                  <td>{item.input_i18n?.english || '—'}</td>
                  <td>{item.dose || '—'}</td>
                  <td>{item.state_name || 'All States'}</td>
                  <td>
                    {item.min_age !== '' || item.max_age !== '' 
                      ? `${item.min_age !== '' ? item.min_age : 0} - ${item.max_age !== '' ? item.max_age : '∞'} yrs` 
                      : 'All Ages'}
                  </td>
                  <td>{Array.isArray(item.stage_indices) ? item.stage_indices.join(', ') : item.stage_indices || '—'}</td>
                  <td>
                    {!viewMode && (
                      <>
                        <Button variant="outline-primary" size="sm" className="me-1" onClick={() => openEdit(item)}>Edit</Button>
                        <Button variant="outline-danger" size="sm" onClick={() => window.confirm('Delete?') && onDelete(item.id)}>Delete</Button>
                      </>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <Modal show={showModal} onHide={() => setShowModal(false)} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>{editingId ? 'Edit' : 'Add'} item</Modal.Title>
        </Modal.Header>
        <form onSubmit={handleSubmit}>
          <Modal.Body>
            <div className="row mb-3">
              <div className="col-md-4">
                <label className="form-label fw-bold">Applicable State</label>
                <select 
                  className="form-select" 
                  value={form.state_id || ''} 
                  onChange={(e) => setForm((f) => ({ ...f, state_id: e.target.value }))}
                >
                  <option value="">All States</option>
                  {states?.map((st) => (
                    <option key={st.id} value={st.id}>
                      {st.name_en}
                    </option>
                  ))}
                </select>
                <small className="text-muted">Leave blank for standard</small>
              </div>
              <div className="col-md-4">
                <label className="form-label fw-bold">Min Age (Years)</label>
                <input 
                  type="number" 
                  step="0.01"
                  min="0"
                  className="form-control" 
                  placeholder="e.g. 0"
                  value={form.min_age} 
                  onChange={(e) => setForm((f) => ({ ...f, min_age: e.target.value }))} 
                />
              </div>
              <div className="col-md-4">
                <label className="form-label fw-bold">Max Age (Years)</label>
                <input 
                  type="number" 
                  step="0.01"
                  min="0"
                  className="form-control" 
                  placeholder="e.g. 5.5"
                  value={form.max_age} 
                  onChange={(e) => setForm((f) => ({ ...f, max_age: e.target.value }))} 
                />
              </div>
            </div>
            <div className="mb-2">
              <label className="form-label fw-bold">Input</label>
              {visibleLangs.map((lang) => (
                <div key={lang} className="input-group input-group-sm mb-1">
                  <span className="input-group-text text-capitalize" style={{ width: '80px' }}>{lang}</span>
                  <input type="text" className="form-control" placeholder={`Enter in ${lang}...`} value={form.input_i18n[lang] || ''} onChange={(e) => setI18n('input_i18n', lang, e.target.value)} />
                </div>
              ))}
            </div>
            <div className="row mb-2">
              <div className="col-md-4">
                <label className="form-label">Dose</label>
                <input type="text" className="form-control" value={form.dose} onChange={(e) => setForm((f) => ({ ...f, dose: e.target.value }))} />
              </div>
              <div className="col-md-4">
                <label className="form-label">Per session dose</label>
                <input type="text" className="form-control" value={Array.isArray(form.per_session_dose) ? form.per_session_dose.join(', ') : form.per_session_dose} onChange={(e) => setForm((f) => ({ ...f, per_session_dose: e.target.value }))} />
              </div>
              <div className="col-md-4 d-flex align-items-end pb-1">
                <div className="form-check">
                  <input 
                    className="form-check-input" 
                    type="checkbox" 
                    id="allListConstantDoseCheck"
                    checked={form.constant_dose} 
                    onChange={(e) => setForm((f) => ({ ...f, constant_dose: e.target.checked }))} 
                  />
                  <label className="form-check-label" htmlFor="allListConstantDoseCheck">
                    Constant Dose
                  </label>
                </div>
              </div>
            </div>
            <div className="mb-2">
              <label className="form-label fw-bold">Unit</label>
              {visibleLangs.map((lang) => (
                <div key={lang} className="input-group input-group-sm mb-1">
                  <span className="input-group-text text-capitalize" style={{ width: '80px' }}>{lang}</span>
                  <input type="text" className="form-control" placeholder={`Enter in ${lang}...`} value={form.unit_i18n[lang] || ''} onChange={(e) => setI18n('unit_i18n', lang, e.target.value)} />
                </div>
              ))}
            </div>
            <div className="mb-2">
              <label className="form-label fw-bold">Method</label>
              {visibleLangs.map((lang) => (
                <div key={lang} className="input-group input-group-sm mb-1">
                  <span className="input-group-text text-capitalize" style={{ width: '80px' }}>{lang}</span>
                  <input type="text" className="form-control" placeholder={`Enter in ${lang}...`} value={form.method_i18n[lang] || ''} onChange={(e) => setI18n('method_i18n', lang, e.target.value)} />
                </div>
              ))}
            </div>
            <div className="mb-2">
              <label className="form-label">Stage indices (comma-separated, 1-based)</label>
              <input type="text" className="form-control" value={stageIndicesInput} onChange={(e) => setStageIndicesInput(e.target.value)} placeholder="1, 2, 4" />
            </div>
            {message && <div className="text-danger small">{message}</div>}
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={() => setShowModal(false)}>Cancel</Button>
            <Button type="submit" variant="primary" disabled={saving}>{saving ? 'Saving…' : 'Save'}</Button>
          </Modal.Footer>
        </form>
      </Modal>
    </div>
  );
};

export default CropAllList;